import React, { useEffect, useState } from "react"

interface Choice {
  color: string
  text: string
}

interface ColorMatchProps {
  question: string
  rounds: {
    instruction: string
    targetColor: string
    targetText: string
    choices: Choice[]
  }[]
  onComplete: (score: number) => void
}

const ColorMatch: React.FC<ColorMatchProps> = ({ question, rounds, onComplete }) => {
  const [currentRound, setCurrentRound] = useState(0)
  const [score, setScore] = useState(0)
  const [isStarted, setIsStarted] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [shuffledChoices, setShuffledChoices] = useState<Choice[]>([])
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setCurrentRound(0)
    setIsStarted(false)
    setIsComplete(false)
    // Don't reset score here as it needs to accumulate across rounds
    setSelectedChoiceIndex(null)
  }, [question])

  // Shuffle the choices for the current round
  useEffect(() => {
    if (isStarted && currentRound < rounds.length) {
      setShuffledChoices([...rounds[currentRound].choices].sort(() => Math.random() - 0.5))
    }
  }, [currentRound, isStarted, rounds])

  const startTask = (e: React.FormEvent) => {
    e.preventDefault()
    setIsStarted(true)
    setCurrentRound(0)
    setScore(0) // Initialize score at the start of the task
    setIsComplete(false)
    setSelectedChoiceIndex(null)
  }

  const finishTask = () => {
    setIsComplete(true)
    // Make sure we're passing the final score after all rounds are complete
    onComplete(score)
  }

  const handleChoice = (choice: Choice, index: number) => {
    let isCorrect = false

    // Different logic for each round
    if (currentRound === 0) {
      // Round 1: Click where COLOR matches TEXT
      isCorrect = choice.color === choice.text.toLowerCase()
    } else if (currentRound === 1) {
      // Round 2: Click where TEXT describes COLOR
      isCorrect = choice.text.toLowerCase() === choice.color
    } else if (currentRound === 2) {
      // Round 3: Click where COLOR is NOT the same as TEXT
      isCorrect = choice.color !== choice.text.toLowerCase()
    }

    // Set the selected choice without showing feedback
    setSelectedChoiceIndex(index)

    // Update score: +1 for correct, -0.5 for incorrect
    if (isCorrect) {
      setScore((prev) => prev + 1)
    } else {
      setScore((prev) => Math.max(0, prev - 0.5)) // Prevent negative scores
    }

    // Move to next round after a brief delay, regardless of correctness
    setTimeout(() => {
      setSelectedChoiceIndex(null)

      // Always move to next round
      if (currentRound < rounds.length - 1) {
        // Show loading indicator before moving to next round
        setIsLoading(true)
        setTimeout(() => {
          setCurrentRound((prev) => prev + 1)
          setIsLoading(false)
        }, 1000)
      } else {
        finishTask()
      }
    }, 1000)
  }

  const getTextColor = (choice: Choice) => {
    // For visual contrast, use white text on dark backgrounds
    const darkColors = ["blue", "green", "red", "purple", "black"]
    return darkColors.includes(choice.color) ? "white" : "black"
  }

  return (
    <div className="space-y-6">
      {!isStarted ? (
        <div className="text-center">
          <h2 className="mb-6 text-left text-2xl">{question}</h2>
          <button
            onClick={startTask}
            className="rounded-lg bg-[#0F1D21] px-6 py-2 font-bold text-white shadow-md transition-colors duration-300 hover:bg-gray-700"
          >
            Start Task
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {!isComplete && currentRound < rounds.length && (
            <>
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="h-16 w-16 animate-spin rounded-full border-4 border-b-[#0F1D21] border-l-transparent border-r-[#0F1D21] border-t-[#0F1D21]"></div>
                  <p className="mt-4 text-lg font-medium">Loading next round...</p>
                </div>
              ) : (
                <>
                  {/* Round Instruction */}
                  <div className="rounded-lg bg-gray-100 p-4">
                    <p className="text-center text-xl font-semibold">
                      {rounds[currentRound].instruction}
                    </p>
                  </div>

                  {/* Color Boxes */}
                  <div className="my-8 grid grid-cols-2 gap-4">
                    {shuffledChoices.map((choice, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.preventDefault()
                          // Only allow selection if no selection is currently shown
                          if (selectedChoiceIndex === null) {
                            handleChoice(choice, index)
                          }
                        }}
                        className={`flex h-24 w-full items-center justify-center rounded-lg shadow-md transition-all ${
                          selectedChoiceIndex !== null && selectedChoiceIndex !== index
                            ? "opacity-50"
                            : "hover:scale-105"
                        }`}
                        style={{
                          backgroundColor: choice.color,
                          opacity: selectedChoiceIndex === index ? 0.7 : 1,
                          transform: selectedChoiceIndex === index ? "scale(0.95)" : "scale(1)",
                          transition: "all 0.2s ease-in-out",
                        }}
                        disabled={selectedChoiceIndex !== null}
                      >
                        <span className="text-xl font-bold" style={{ color: getTextColor(choice) }}>
                          {choice.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Complete state */}
          {isComplete && (
            <div className="mt-6 text-center">
              <p className="text-xl font-bold">Task Complete!</p>
              <p className="text-md text-gray-600">You completed all {rounds.length} rounds.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ColorMatch
